import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, TimerReset } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/stores/auth";

const LOGOUT_REDIRECT_PATH = "/logout/";
const LOGOUT_COUNTDOWN_SECONDS = 10;

function redirectToLogout(clearSessionExpiry: () => void) {
  clearSessionExpiry();
  window.location.assign(LOGOUT_REDIRECT_PATH);
}

export function SessionExpiredDialog() {
  const sessionExpiryNotice = useAuth((state) => state.sessionExpiryNotice);
  const clearSessionExpiry = useAuth((state) => state.clearSessionExpiry);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!sessionExpiryNotice) return;

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [sessionExpiryNotice]);

  const secondsRemaining = useMemo(() => {
    if (!sessionExpiryNotice) return LOGOUT_COUNTDOWN_SECONDS;
    const elapsedMs = now - sessionExpiryNotice.startedAt;
    const remaining = Math.ceil((LOGOUT_COUNTDOWN_SECONDS * 1000 - elapsedMs) / 1000);
    return Math.max(0, remaining);
  }, [now, sessionExpiryNotice]);

  useEffect(() => {
    if (!sessionExpiryNotice || secondsRemaining > 0) return;
    redirectToLogout(clearSessionExpiry);
  }, [clearSessionExpiry, secondsRemaining, sessionExpiryNotice]);

  if (!sessionExpiryNotice) return null;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md border-rose/25 bg-surface shadow-[0_30px_80px_-20px_rgba(146,36,58,0.45)] [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="flex items-start gap-3 pr-8">
          <div className="mt-0.5 rounded-xl bg-rose/12 p-2 text-rose">
            <AlertTriangle size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle>Session expired</DialogTitle>
            <DialogDescription className="mt-2">
              {sessionExpiryNotice.reason}
            </DialogDescription>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-line bg-surface-2/70 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-fg-mute">
                Logout countdown
              </p>
              <p className="mt-1 text-sm text-fg-dim">
                Redirecting to the logout page to end this session cleanly.
              </p>
            </div>
            <div className="inline-flex min-w-14 items-center justify-center gap-1 rounded-2xl border border-rose/20 bg-rose/10 px-3 py-2 font-mono text-xl font-bold text-rose">
              <TimerReset size={16} />
              <span>{secondsRemaining}</span>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button variant="danger" onClick={() => redirectToLogout(clearSessionExpiry)}>
            Log out now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
